// Team inferiority (short-handed) queue — the sequential SUM of a team's personal
// penalties (002 spec "Team Inferiority — Detailed Rules", FR-017/035/036/037/038).
//
// Modeled as an ordered array of segments, each a clock `{ durationMs, startedAt }`
// (see clock.js). Only the FRONT segment runs (startedAt set); the rest are queued
// (startedAt null → frozen at full duration). Because a paused clock's remaining time
// equals its full duration, the displayed inferiority is simply the sum of every
// segment's remaining time — i.e. the sequential sum, which drains one segment after
// another. This is distinct from the team's personal penalties, which run in parallel
// (see penalties.js); the inferiority can therefore OUTLAST the visible penalties.

import { remainingMs, startClock, stopClock } from './clock.js';

/** Total inferiority time (ms): front segment's remaining + full duration of queued ones. */
export function totalInferiorityMs(queue, now) {
  return queue.reduce((sum, seg) => sum + remainingMs(seg, now), 0);
}

/** Append a queued segment (a newly added penalty's contribution). */
export function appendSegment(queue, durationMs) {
  return [...queue, { durationMs: Math.max(0, durationMs), startedAt: null }];
}

/** Ensure the front segment is running at `now` (called when the match clock starts). */
export function startFront(queue, now) {
  if (queue.length === 0 || queue[0].startedAt !== null) return queue;
  return [startClock(queue[0], now), ...queue.slice(1)];
}

/** Freeze the front segment at `now` (called when the match clock pauses) — preserves remaining (FR-038). */
export function pauseFront(queue, now) {
  if (queue.length === 0 || queue[0].startedAt === null) return queue;
  return [stopClock(queue[0], now), ...queue.slice(1)];
}

/**
 * Advance the queue: if the running front segment has reached 0, drop it and start the
 * next one at `now`. Repeats for any segments that are already at 0. Sequential drain.
 */
export function advance(queue, now) {
  let q = queue;
  while (q.length > 0 && q[0].startedAt !== null) {
    const expiry = q[0].startedAt + q[0].durationMs;
    if (now < expiry) break; // front still running
    q = q.slice(1);
    // The next segment starts exactly when the previous expired (not at `now`), so a late
    // tick never loses time — the sequential drain stays exact.
    if (q.length > 0 && q[0].startedAt === null) q = [startClock(q[0], expiry), ...q.slice(1)];
  }
  return q;
}

/** Remove the active (front) penalty's segment; promote the next and start it if running (FR-036). */
export function removeFront(queue, now, running) {
  if (queue.length === 0) return queue;
  const rest = queue.slice(1);
  if (rest.length > 0 && rest[0].startedAt === null && running) {
    return [startClock(rest[0], now), ...rest.slice(1)];
  }
  return rest;
}

/** Remove a queued (non-front) segment by index (manual deletion of a queued penalty). */
export function removeAt(queue, index) {
  if (index <= 0 || index >= queue.length) return queue; // front removal goes through removeFront
  return queue.filter((_, i) => i !== index);
}

/**
 * Adjust the front segment by a delta (ms), clamped at 0 — penalty-time edit (FR-035).
 * Shifting `durationMs` shifts the remaining time by the same delta whether the segment is
 * running (remaining = durationMs − elapsed) or paused, so one branch covers both.
 */
export function editFront(queue, deltaMs) {
  return editAt(queue, 0, deltaMs);
}

/**
 * Adjust the segment at `index` by a delta (ms), clamped at 0. Used before the match starts,
 * where each penalty still maps 1:1 to its inferiority segment by position, so editing penalty
 * `i` shifts segment `i` (FR-035 initial-setup edits). After start, only the front is editable
 * (see editFront / the store's editPenaltyTime guard).
 */
export function editAt(queue, index, deltaMs) {
  if (index < 0 || index >= queue.length) return queue;
  return queue.map((seg, i) => (i === index ? { ...seg, durationMs: Math.max(0, seg.durationMs + deltaMs) } : seg));
}
